"use strict"

import Snap from "snapsvg-cjs"

import GestureManager from "./gesture-manager"
import Point from "./point"
import Debouncer from "../util/debouncer"
import SvgSheetLayers from "./svg-sheet-layers"
import SvgCanvas from "./svg-canvas"
import { ZoomConstants } from "../../services/toolbar/zoom-manager"
import FocusManagerService from "../../services/focus-manager/focus-manager.service"
import ToolbarService from "../../services/toolbar/toolbar.service"

const DEFAULT_ZOOM = 1.25

export default class SvgSheet extends SvgCanvas {
  /**
   * @param {FocusManagerService} FocusManagerService
   * @param {ToolbarService} ToolbarService
   */
  constructor(element, $timeout, $window, FocusManagerService, ToolbarService) {
    super()

    this.$timeout = $timeout
    this._window = angular.element($window)

    /** @type {FocusManagerService} */
    this._focusManager = FocusManagerService
    /** @type {ToolbarService} */
    this._toolbarService = ToolbarService
    /** @type {ClipboardManager} */
    this._clipboardManager = ToolbarService.clipboardManager

    this._width = undefined
    this._hiddenTextInput = undefined
    this._svg = element.find("svg")[0]
    this._snap = new Snap(this._svg)
    this._layers = new SvgSheetLayers(this._snap)
    this._element = element
    this._zoomFactor = DEFAULT_ZOOM
    this._initialWidth = element.width()
    this._initialHeight = element.height()
    this._pinchStartState = {}

    this._scaleFactor = 1
    this._inverseScaleFactor = 1
    this._absolutePosition = new Point(0, 0)
    this._scrollViewAbsolutePosition = new Point(0, 0)

    /*
      When the SvgSheet is loaded initially, we want to set the width, scale factors, and the position

      When the SvgSheet is resized, we want to reset the width, scale factors and position

      When the SvgSheet is scrolled, we want to reset just the position
    */

    this.refreshLayout()

    this._resize = new Debouncer(50, 750, () => {
      this.width = element.width()
      this.absolutePosition = element.offset()
      this._initialHeight = element.height()
      this._initialWidth = element.width()

      if (this._scrollView) {
        this.scrollViewAbsolutePosition = this._scrollView.offset()
      }
    })
    this._anonResize = () => {
      this._resize.tick()
    }
    this._window.on("resize", this._anonResize)

    this._scroll = new Debouncer(50, 750, () => {
      this.absolutePosition = this._element.offset()
    })

    this._anonScrollListener = () => this._onContainerScrolled()
    this._anonPinchStart = (event) => this._pinchStart(event)
    this._anonPinchMove = (event) => this._pinchMove(event)
    this._anonPinchEnd = (event) => this._pinchEnd(event)
    this._gesture = new GestureManager(undefined, this)

    this._oneFingerScrolling = false
    this._lastScrollPosition = null

    this._element.on("touchstart", this._anonPinchStart)
  }

  set readonly(value) {
    if (!value) {
      this._gesture.start(this._element)
      this._gesture.click.subscribe(this.handleFallThrough, this)
      this._gesture.dragStart.subscribe(this._onDragStart, this)
      this._gesture.dragMove.subscribe(this._onDragMove, this)
      this._gesture.dragEnd.subscribe(this._onDragEnd, this)
      if (this._clipboardManager) {
        this._clipboardManager.enable()
      }
    } else {
      this._gesture.stop()
      this._gesture.click.unsubscribe(this.handleFallThrough, this)
      this._gesture.dragStart.unsubscribe(this._onDragStart, this)
      this._gesture.dragMove.unsubscribe(this._onDragMove, this)
      this._gesture.dragEnd.unsubscribe(this._onDragEnd, this)
      if (this._clipboardManager) {
        this._clipboardManager.disable()
      }
    }
  }

  _onContainerScrolled() {
    if (this._oneFingerScrolling) return
    this._scroll.tick()
  }

  refreshLayout() {
    this.$timeout(
      () => {
        this.width = this._element.width()
        this.absolutePosition = this._element.offset()
      },
      0,
      false,
    )
  }

  showSheet() {
    this._element.css({
      visibility: "inherit",
    })
  }

  get toFitFactor() {
    return this.scrollViewWidthFactor
  }

  /**
   * Fits to view width but prevents growing past the default zoom factor
   */
  get noGrowFactor() {
    return Math.min(this.scrollViewWidthFactor - 0.05, DEFAULT_ZOOM)
  }

  get initialWidthFactor() {
    return this._initialWidth / SvgCanvas.INITIAL_WIDTH
  }

  get scrollViewWidthFactor() {
    return this._scrollView
      ? this._scrollView.width() / SvgCanvas.INITIAL_WIDTH
      : DEFAULT_ZOOM
  }

  /**
   * Zooms the canvas to the specified zoom factor
   * @param zoomFactor
   */
  zoom(zoomFactor) {
    if (!this._initialHeight || !this._initialWidth || !this._scrollView) return

    let newHeight = SvgCanvas.INITIAL_HEIGHT * zoomFactor
    let newWidth = SvgCanvas.INITIAL_WIDTH * zoomFactor

    this._zoomFactor = zoomFactor

    this._element.css({
      height: newHeight,
      width: newWidth,
    })

    this.refreshLayout()
  }

  _pinchStart(event) {
    if (!this._scrollView) return
    if (event.originalEvent.touches.length < 2) return

    event.preventDefault()
    event.stopPropagation()

    this.readonly = true

    this._savePinchState(event)
    this._element.on("touchmove", this._anonPinchMove)
    this._element.on("touchend", this._anonPinchEnd)
    this._element.on("touchcancel", this._anonPinchEnd)
  }

  _pinchMove(event) {
    const startTouches = this._pinchStartState.touches
    const moveTouches = event.originalEvent.touches

    if (startTouches.length < 2 || moveTouches.length < 2) return

    event.preventDefault()
    event.stopPropagation()

    // calculate new zoom
    const startDist = this._getTouchDistance(startTouches)
    const moveDist = this._getTouchDistance(moveTouches)
    let newZoom = this._pinchStartState.zoomFactor * (moveDist / startDist)
    newZoom = Math.max(
      this.noGrowFactor,
      Math.min(ZoomConstants.UPPER_LIMIT, newZoom),
    )

    // update scroll to keep content under fingers
    const moveCenter = this._getTouchCenter(moveTouches).minusOffset(
      this._scrollView.offset(),
    )
    const newScrollLeft =
      this._pinchStartState.contentCenter.x * newZoom - moveCenter.x
    const newScrollTop =
      this._pinchStartState.contentCenter.y * newZoom - moveCenter.y

    this.zoom(newZoom)
    this._toolbarService.zoomFactor = newZoom

    this._scrollView.scrollLeft(newScrollLeft)
    this._scrollView.scrollTop(newScrollTop)
  }

  _pinchEnd(event) {
    event.preventDefault()
    event.stopPropagation()

    this.readonly = false

    this._pinchStartState = {}
    this._element.off("touchcancel", this._anonPinchEnd)
    this._element.off("touchend", this._anonPinchEnd)
    this._element.off("touchmove", this._anonPinchMove)
  }

  _savePinchState(event) {
    const touches = event.originalEvent.touches

    if (touches.length < 2) {
      console.warn("Not enough touches to save pinch state")
      return
    }

    // calculate the center of the pinch relative to content
    const touchCenter = this._getTouchCenter(touches)
    const scrollViewOffset = this._scrollView.offset()
    const contentCenter = new Point(
      (touchCenter.x - scrollViewOffset.left + this._scrollView.scrollLeft()) /
        this._zoomFactor,
      (touchCenter.y - scrollViewOffset.top + this._scrollView.scrollTop()) /
        this._zoomFactor,
    )

    this._pinchStartState.touches = touches
    this._pinchStartState.contentCenter = contentCenter
    this._pinchStartState.zoomFactor = this._zoomFactor
  }

  /**
   * Calculates the center point between two touch points
   * @param touches {TouchList}
   * @return {Point | null}
   */
  _getTouchCenter(touches) {
    if (touches.length < 2) return null

    const x = (touches[0].clientX + touches[1].clientX) / 2
    const y = (touches[0].clientY + touches[1].clientY) / 2

    return new Point(x, y)
  }

  /**
   * Calculates the distance between two touch points
   * @param touches {TouchList}
   * @return {number}
   */
  _getTouchDistance(touches) {
    if (touches.length < 2) return 0

    const dx = touches[0].clientX - touches[1].clientX
    const dy = touches[0].clientY - touches[1].clientY

    return Math.hypot(dx, dy)
  }

  /**
   * Handles the start of a one-finger drag for scrolling
   * @param args - Event arguments from GestureManager
   */
  _onDragStart(args) {
    if (
      !this._scrollView ||
      !args.originalEvent ||
      args.originalEvent.type.indexOf("touch") === -1 ||
      args.originalEvent.touches?.length !== 1
    ) {
      return
    }

    this._oneFingerScrolling = true
    this._lastScrollPosition = new Point(
      this._scrollView.scrollLeft(),
      this._scrollView.scrollTop(),
    )
  }

  /**
   * Handles one-finger drag movement for scrolling
   * @param args - Event arguments from GestureManager
   */
  _onDragMove(args) {
    if (!this._oneFingerScrolling || !this._scrollView || !args.delta) {
      return
    }

    // Invert the delta to make scrolling feel natural (drag down = scroll up)
    const deltaX = -args.delta.x * this._zoomFactor
    const deltaY = -args.delta.y * this._zoomFactor

    const newScrollLeft = this._lastScrollPosition.x + deltaX
    const newScrollTop = this._lastScrollPosition.y + deltaY

    this._scrollView.scrollLeft(newScrollLeft)
    this._scrollView.scrollTop(newScrollTop)
  }

  /**
   * Handles the end of a one-finger drag for scrolling
   * @param args - Event arguments from GestureManager
   */
  _onDragEnd(args) {
    this._oneFingerScrolling = false
    this._lastScrollPosition = null
    this.absolutePosition = this._element.offset()
  }

  handleFallThrough() {
    this._focusManager.focusedElement = undefined
  }

  /**
   * @return {SvgSheetLayers}
   */
  get layers() {
    return this._layers
  }

  get width() {
    return this._width
  }

  set width(value) {
    this._width = value > 0 ? value : this._width
    this._scaleFactor = this._width / SvgCanvas.INITIAL_WIDTH
    this._inverseScaleFactor = SvgCanvas.INITIAL_WIDTH / this._width
  }

  get scaleFactor() {
    return this._scaleFactor
  }

  get inverseScaleFactor() {
    return this._inverseScaleFactor
  }

  get absolutePosition() {
    return this._absolutePosition
  }

  set absolutePosition(position) {
    this._absolutePosition = new Point(position.left, position.top)
  }

  get scrollViewAbsolutePosition() {
    return this._scrollViewAbsolutePosition
  }

  set scrollViewAbsolutePosition(position) {
    this._scrollViewAbsolutePosition = new Point(position.left, position.top)
  }

  set scrollViewSelector(value) {
    if (this._scrollView) {
      this._scrollView.off("scroll", this._anonScrollListener)
      this._scrollView = undefined
    }

    this._scrollViewSelector = value

    if (this._scrollViewSelector) {
      this._scrollView = angular.element(this._scrollViewSelector)
      this.scrollViewAbsolutePosition = this._scrollView.offset()
      this._scrollView.on("scroll", this._anonScrollListener)
    }
  }

  destroy() {
    this._resize.destroy()
    this._scroll.destroy()
    this._gesture.stop()

    this.scrollViewSelector = undefined

    this._window.off("resize", this._anonResize)
    this._element.off("touchstart", this._anonPinchStart)
    this._element.off("touchmove", this._anonPinchMove)
    this._element.off("touchend", this._anonPinchEnd)
    this._element.off("touchcancel", this._anonPinchEnd)
    this._scrollView?.off("scroll", this._anonScrollListener)
  }

  /**
   * @param position {Point}
   * @returns {Point}
   */
  scrollOffset(position) {
    return position.minus(
      new Point(this.absolutePosition.x, this.absolutePosition.y),
    )
  }

  /**
   * Gives the origin of the canvas in device pixels
   * @returns {Point}
   */
  get canvasOrigin() {
    let origin = this.absolutePosition.minus(this.scrollViewAbsolutePosition)
    return new Point(Math.min(origin.x, 0), Math.min(origin.y, 0))
  }

  /**
   * Gives the origin of the canvas in canvas pixels
   * @returns {Point}
   */
  get scaledCanvasOrigin() {
    return this.canvasOrigin.times(this.inverseScaleFactor)
  }

  elementOffset(position) {
    return position
      .times(Math.min(1, this.inverseScaleFactor))
      .minus(this.scaledCanvasOrigin)
  }

  /**
   * @param position {Point}
   * @returns {Point}
   */
  scale(position) {
    return position.times(this.scaleFactor)
  }

  /**
   * @param position {Point}
   * @returns {Point}
   */
  inverseScale(position) {
    return position.times(this.inverseScaleFactor)
  }

  get hiddenTextInput() {
    if (!this._hiddenTextInput) {
      this._hiddenTextInput = this._element.find(".hidden-text-input")
    }
    return this._hiddenTextInput
  }
}
